import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Phone, PhoneOutgoing, User, Search, RefreshCw, Clock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/contexts/AuthContext'
import { useInfiniteCallHistoryQuery } from '@/hooks/useInfiniteCallHistoryQuery'
import { useContactLookup } from '@/hooks/useContactLookup'
import { usePullToRefresh } from '@/hooks/usePullToRefresh'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { CallHistoryItem, Contact } from '@/types/account'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { formatDuration, formatTimeAgo } from '@/lib/format'
import { AvatarPlaceholder } from '@/components/ui/avatar-placeholder'
import { PhoneNumber } from '@/components/ui/phone-number'
import AuthOverlay from './AuthOverlay'
import PullToRefreshIndicator from './PullToRefreshIndicator'
import { ContactDetailState } from './ContactDetailPanel'

const ROW_HEIGHT = 64

// ---------------------------------------------------------------------------
// HistoryPanel
// ---------------------------------------------------------------------------

interface HistoryPanelProps {
  className?: string
  onOpenContact?: (detail: ContactDetailState) => void
  onCall?: (number: string) => void
}

const HistoryPanel: React.FC<HistoryPanelProps> = ({ className, onOpenContact, onCall }) => {
  const { t } = useTranslation(['app', 'common'])
  const { isAuthenticated } = useAuth()
  const isDesktop = useBreakpoint('lg')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedQuery(searchQuery), 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [searchQuery])

  const {
    calls,
    totalItems,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refresh,
  } = useInfiniteCallHistoryQuery({ query: debouncedQuery || undefined })

  const { lookup } = useContactLookup()

  useEffect(() => { if (error) toast.error(error.message) }, [error])

  // Pull-to-refresh (mobile)
  const { state: pullState, pullDistance, isRefreshing } = usePullToRefresh(scrollContainerRef, {
    onRefresh: refresh,
    disabled: isDesktop,
  })

  // Pre-sized virtualizer — count = totalItems keeps height stable
  const virtualCount = totalItems > 0 ? totalItems : calls.length

  const rowVirtualizer = useVirtualizer({
    count: virtualCount,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  })

  const virtualItems = rowVirtualizer.getVirtualItems()
  const lastVirtualItemIndex = virtualItems.length > 0 ? virtualItems[virtualItems.length - 1].index : -1

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return
    if (lastVirtualItemIndex >= calls.length - 1) fetchNextPage()
  }, [lastVirtualItemIndex, calls.length, hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleRowClick = useCallback((call: CallHistoryItem) => {
    const contact = lookup(call.destinationNumber)
    onOpenContact?.({ contact: contact ?? null, phoneNumber: call.destinationNumber })
  }, [lookup, onOpenContact])

  const handleCall = useCallback((number: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onCall?.(number)
  }, [onCall])

  return (
    <AuthOverlay isAuthenticated={isAuthenticated} className={cn('h-full flex flex-col', className)} onLoginSuccess={() => refresh()}>
      {/* Header */}
      <div className="p-6 border-b border-border bg-background sticky top-0 z-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold text-foreground">Recents</h2>
            {totalItems > 0 && <Badge variant="secondary">{totalItems}</Badge>}
          </div>
          {isDesktop && (
            <Button variant="ghost" size="sm" onClick={refresh} disabled={isFetchingNextPage || isLoading}>
              <RefreshCw className={cn('w-4 h-4', (isFetchingNextPage || isRefreshing) && 'animate-spin')} />
            </Button>
          )}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Scroll container */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-scroll min-h-0">
        {/* Pull-to-refresh indicator (mobile) */}
        {!isDesktop && (
          <PullToRefreshIndicator state={pullState} pullDistance={pullDistance} />
        )}

        {isLoading ? (
          <div className="divide-y divide-border">
            {Array.from({ length: 5 }).map((_, i) => <SkeletonCallRow key={i} />)}
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <Button variant="outline" size="sm" onClick={refresh}>{t('common.retry')}</Button>
          </div>
        ) : virtualCount === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Clock className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {debouncedQuery ? t('common.noResults') : t('account.noCallHistory')}
              </p>
            </div>
          </div>
        ) : (
          <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
            {virtualItems.map((virtualRow) => {
              const call = calls[virtualRow.index]
              return (
                <div
                  key={virtualRow.key}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  {call
                    ? <CallRow
                        call={call}
                        contact={lookup(call.destinationNumber)}
                        onRowClick={handleRowClick}
                        onCall={handleCall}
                      />
                    : <SkeletonCallRow />
                  }
                </div>
              )
            })}
          </div>
        )}
      </div>
    </AuthOverlay>
  )
}

// ---------------------------------------------------------------------------
// SkeletonCallRow
// ---------------------------------------------------------------------------

const SkeletonCallRow: React.FC = () => (
  <div className="px-4 flex items-center gap-3 border-b border-border" style={{ height: ROW_HEIGHT }}>
    <Skeleton className="w-9 h-9 rounded-full shrink-0" />
    <div className="flex-1 space-y-2">
      <Skeleton className="h-3.5 w-32" />
      <Skeleton className="h-3 w-24" />
    </div>
    <Skeleton className="h-3 w-10 mr-2" />
    <Skeleton className="w-8 h-8 rounded-full shrink-0" />
  </div>
)

// ---------------------------------------------------------------------------
// CallRow — redesigned with avatar, cleaner layout, always-visible call btn
// ---------------------------------------------------------------------------

interface CallRowProps {
  call: CallHistoryItem
  contact: Contact | undefined
  onRowClick: (call: CallHistoryItem) => void
  onCall: (number: string, e: React.MouseEvent) => void
}

const CallRow: React.FC<CallRowProps> = React.memo(({ call, contact, onRowClick, onCall }) => {
  const { t } = useTranslation(['app', 'common'])

  const displayName = contact
    ? [contact.firstName, contact.lastName].filter(Boolean).join(' ') || t('contacts.unnamed')
    : null

  const avatarName = displayName ?? call.destinationNumber
  const duration = formatDuration(call.duration)
  const timeAgo = formatTimeAgo(call.startTime, t('account.justNow'))

  return (
    <button
      onClick={() => onRowClick(call)}
      className="w-full flex items-center gap-3 px-4 hover:bg-muted/50 transition-colors border-b border-border text-left"
      style={{ height: ROW_HEIGHT }}
    >
      {/* Avatar */}
      {contact
        ? <AvatarPlaceholder seed={avatarName} size={36} className="flex-shrink-0" />
        : <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-muted">
            <User className="w-4 h-4 text-muted-foreground" />
          </div>
      }

      {/* Name + number */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground truncate leading-tight">
          {displayName ?? <PhoneNumber number={call.destinationNumber} />}
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <PhoneOutgoing className="w-3 h-3 text-muted-foreground shrink-0" />
          <PhoneNumber number={call.destinationNumber} className="text-xs text-muted-foreground truncate" />
        </div>
      </div>

      {/* Time + duration */}
      <div className="text-right shrink-0 mr-1">
        <div className="text-xs text-muted-foreground">{timeAgo}</div>
        {duration && <div className="text-xs text-muted-foreground">{duration}</div>}
      </div>

      {/* Call button — always visible */}
      <div
        onClick={(e) => onCall(call.destinationNumber, e)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onCall(call.destinationNumber, e as any) }}
        className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-colors cursor-pointer"
        title={t('contacts.call')}
      >
        <Phone className="w-3.5 h-3.5 text-primary" />
      </div>
    </button>
  )
})
CallRow.displayName = 'CallRow'

export default HistoryPanel
